import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import crossFetch from 'cross-fetch';
import {
  ACLObject,
  ACLRequestObject,
  ACLResource,
  ACLSystemOwned,
  GetACLBySystemUrnResponse,
  RequestAndACLsByOp,
  RequestAndACLsByRef,
} from '../zodSchema/ACLObject';
import { encodeQueryParams } from '@agilelab/plugin-wb-platform-common';
import {
  AccessRequestHookUpdateAction,
  PendingAccessRequestHooksFacets,
  PendingAccessRequestHooksByProductOptions,
  PendingAccessRequestHooksByProductFacetsOptions,
  PendingAccessRequestHooksResponse,
  PendingAccessRequestUpdateResponse,
} from '@agilelab/plugin-wb-action-handler-common';

/**
 * Options you can pass into a request for additional information.
 *
 * @public
 */
export interface RequestOptions {
  token?: string;
}

export interface AccessControlListApi {
  /**
   * @param includeChildACLs if `true`, will also return ACLs for child subcomponents
   */
  getACLs(
    componentExternalId: string,
    environment: string,
    includeChildACLs: boolean,
    options?: RequestOptions,
  ): Promise<ACLObject[]>;

  getACLRequestsByRefs(
    externalId: string,
    environmentName: string,
    refs: string[],
    requestId: string,
    options?: RequestOptions,
  ): Promise<ACLRequestObject[]>;

  getACLRequestRow(
    externalId: string,
    environmentName: string,
    ref: string,
    requestId: string,
    options?: RequestOptions,
  ): Promise<ACLRequestObject>;

  getACLAndRequestsByOp(
    idComponent: string,
    options?: RequestOptions,
  ): Promise<RequestAndACLsByOp>;

  getACLAndRequestsByRef(
    ref: string,
    environment: string,
    dataProductInstanceId?: number,
    options?: RequestOptions,
  ): Promise<RequestAndACLsByRef>;

  getACLSystemsOwnedByRefs(
    refs: string[],
    environment: string,
    options?: RequestOptions,
  ): Promise<ACLSystemOwned[]>;

  getACLBySystemUrn(
    systemUrn: string,
    options?: RequestOptions,
  ): Promise<GetACLBySystemUrnResponse>;

  addACLs(objects: any, options?: RequestOptions): Promise<any>;

  upsertACLRequest(objects: any, options?: RequestOptions): Promise<any>;

  /**
   * Retrieves the details of a resource protected by an ACL, by its external id
   */
  resolveResource(
    externalId: string,
    environment: string,
    options?: RequestOptions,
  ): Promise<ACLResource | undefined>;

  /**
   * Retrieves access request hooks related to a product
   * Only owners or data access managers of the product can use this method
   * @param productInstanceId
   * @param options
   */
  getPendingAccessRequestHooksByProduct(
    productInstanceId: number,
    options?: PendingAccessRequestHooksByProductOptions,
  ): Promise<PendingAccessRequestHooksResponse>;
  /**
   * Retrieves facets containing filters metadata (count, value) for access request hook instances related to a product
   * Only owners or data access managers of the product can use this method
   * @param productInstanceId
   * @param options
   */
  getPendingAccessRequestHooksByProductFilterFacets(
    productInstanceId: number,
    options?: PendingAccessRequestHooksByProductFacetsOptions,
  ): Promise<PendingAccessRequestHooksFacets>;
  /**
   * Updates the status of an access control notification hook with the designated action
   * Only owners or data access managers of the product related to the hook can use this method
   * @param hookId
   * @param action
   */
  updatePendingAccessRequestHook(
    hookId: string,
    action: AccessRequestHookUpdateAction,
  ): Promise<PendingAccessRequestUpdateResponse>;
}

export class AccessControlListClient implements AccessControlListApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: { getBaseUrl(pluginId: string): Promise<string> };
    fetchApi?: { fetch: typeof fetch };
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
  }

  async addACLs(objects: any, options?: RequestOptions): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');

    const response = await this.fetchApi.fetch(`${baseUrl}/ACL`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
      },
      body: JSON.stringify({ objects: objects }),
    });

    if (!response.ok) {
      throw new Error(`Unable to add ACL, reason: ${response.statusText}`);
    }

    return response.json();
  }

  async upsertACLRequest(objects: any, options?: RequestOptions): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');

    const response = await this.fetchApi.fetch(`${baseUrl}/ACLRequests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
      },
      body: JSON.stringify({ objects: objects }),
    });

    if (!response.ok) {
      throw new Error(
        `Unable to upsert the ACL Requests rows, reason: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getACLs(
    componentExternalId: string,
    environment: string,
    includeChildACLs: boolean,
    options?: RequestOptions,
  ): Promise<ACLObject[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');

    const queryParams = encodeQueryParams({
      includeChildACLs,
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ACL/${environment}/${componentExternalId}${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `This field has been disabled due to a network error. Please try again or contact the platform team. HTTP Response: ${response.statusText}.`,
      );
    }

    return response.json();
  }

  async getACLRequestsByRefs(
    externalId: string,
    environmentName: string,
    refs: string[],
    requestId: string,
    options?: RequestOptions,
  ): Promise<ACLRequestObject[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');

    const data = {
      externalId,
      environmentName,
      refs,
      requestId,
    };

    const response = await this.fetchApi.fetch(`${baseUrl}/ACLRequestsByRefs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(
        `Unable to get ACL_Request with output port externalId ${externalId} and environment ${environmentName}, reason: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getACLRequestRow(
    externalId: string,
    environmentName: string,
    ref: string,
    requestId: string,
    options?: RequestOptions,
  ): Promise<ACLRequestObject> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ACLRequests/${environmentName}/${externalId}/${ref}/${requestId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Unable to get ACL_Request row with output port external id ${externalId}, reason: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getACLAndRequestsByOp(
    idComponent: string,
    options?: RequestOptions,
  ): Promise<RequestAndACLsByOp> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ACLAndRequestsByOp/${idComponent}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Unable to get ACL_Request and ACLs with output port id ${idComponent}, reason: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getACLAndRequestsByRef(
    ref: string,
    environment: string,
    dataProductInstanceId?: number,
    options?: RequestOptions,
  ): Promise<RequestAndACLsByRef> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');
    const query = new URLSearchParams({ ref, environment });
    if (dataProductInstanceId !== undefined) {
      query.set('dataProductInstanceId', dataProductInstanceId.toString());
    }

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ACLAndRequestsByRef?${query.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Unable to get ACL_Request and ACLs with ref ${ref}, reason: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getACLBySystemUrn(
    systemUrn: string,
    options?: RequestOptions,
  ): Promise<GetACLBySystemUrnResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/ACL/by-system/${systemUrn}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Unable to get ACL by system URN ${systemUrn}, reason: ${response.statusText}`,
      );
    }

    const result: ACLObject[] = await response.json();
    return result.flatMap(aclobject => ({
      userRef: aclobject.refs,
      idOutputPort: aclobject.port_id,
    }));
  }

  async getACLSystemsOwnedByRefs(
    refs: string[],
    environment: string,
    options?: RequestOptions,
  ): Promise<ACLSystemOwned[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');
    const body = {
      environment,
      refs,
    };
    const response = await this.fetchApi.fetch(
      `${baseUrl}/ACLSystemsOwnedByRefs`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Unable to get ACL Systems Owned with body ${JSON.stringify(
          body,
        )}, reason: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async resolveResource(
    externalId: string,
    environment: string,
    options?: RequestOptions,
  ): Promise<ACLResource | undefined> {
    const baseUrl = await this.discoveryApi.getBaseUrl('marketplace');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/ACL/${environment}/resources/${externalId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw new Error(
        `Unable to resolve the numeric id for resource ${externalId}, reason: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getPendingAccessRequestHooksByProduct(
    productInstanceId: number,
    options?: PendingAccessRequestHooksByProductOptions,
  ): Promise<PendingAccessRequestHooksResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('actionHandler');

    const filters = options?.filters;
    const pagination = options?.pagination;
    const sort = options?.sort;

    const params = new URLSearchParams();
    for (const v of filters?.consumableInterface ?? []) {
      params.append('consumableInterface', v);
    }
    for (const v of filters?.requester ?? []) {
      params.append('requester', v);
    }
    for (const v of filters?.requestedFor ?? []) {
      params.append('requestedFor', v);
    }
    for (const v of filters?.hookName ?? []) {
      params.append('hookName', v);
    }
    if (pagination?.offset !== undefined) {
      params.set('offset', String(pagination.offset));
    }
    if (pagination?.limit !== undefined) {
      params.set('limit', String(pagination.limit));
    }
    if (sort?.sortBy) {
      params.set('sortBy', sort.sortBy);
    }
    if (sort?.sortOrder) {
      params.set('sortOrder', sort.sortOrder);
    }

    const query = params.toString();
    const url = `${baseUrl}/hooks/access-request/by-product/${productInstanceId}${
      query ? `?${query}` : ''
    }`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.error?.message ??
          `Failed to fetch pending access requests: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getPendingAccessRequestHooksByProductFilterFacets(
    productInstanceId: number,
    options?: PendingAccessRequestHooksByProductFacetsOptions,
  ): Promise<PendingAccessRequestHooksFacets> {
    const baseUrl = await this.discoveryApi.getBaseUrl('actionHandler');

    const filters = options?.filter;

    const params = new URLSearchParams();
    for (const v of filters?.consumableInterface ?? []) {
      params.append('consumableInterface', v);
    }
    for (const v of filters?.requester ?? []) {
      params.append('requester', v);
    }
    for (const v of filters?.requestedFor ?? []) {
      params.append('requestedFor', v);
    }
    for (const v of filters?.hookName ?? []) {
      params.append('hookName', v);
    }
    for (const v of options?.facets ?? []) {
      params.append('facets', v);
    }

    const query = params.toString();
    const url = `${baseUrl}/hooks/access-request/by-product/${productInstanceId}/filter-facets${
      query ? `?${query}` : ''
    }`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.error?.message ??
          `Failed to fetch filter facets: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async updatePendingAccessRequestHook(
    hookId: string,
    action: AccessRequestHookUpdateAction,
  ): Promise<PendingAccessRequestUpdateResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('actionHandler');
    const url = `${baseUrl}/hooks/access-request/by-product/${hookId}/update`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.error?.message ??
          `Failed to process access request: ${response.statusText}`,
      );
    }

    return response.json();
  }
}
